import { useEffect } from 'react';
import { create } from 'zustand';
import api from '../api';

export type IdeaWorkspace = Awaited<ReturnType<typeof api.getIdeaWorkspace>>;

/**
 * One workspace per idea, loaded once and shared by the rail, the phone bar
 * and sheet, and the Next band. Refreshes update in place (no reset to a
 * loading state), so moving between steps never flashes the list.
 */
interface WorkspaceStore {
  byId: Record<string, IdeaWorkspace>;
  load: (ideaId: string) => void;
  refresh: (ideaId: string) => Promise<IdeaWorkspace | null>;
}

const inflight = new Map<string, Promise<IdeaWorkspace | null>>();

export const useIdeaWorkspaceStore = create<WorkspaceStore>((set, get) => ({
  byId: {},
  load: (ideaId) => {
    if (get().byId[ideaId] || inflight.has(ideaId)) return;
    void get().refresh(ideaId);
  },
  refresh: (ideaId) => {
    const running = inflight.get(ideaId);
    if (running) return running;
    const p = api
      .getIdeaWorkspace({ ideaId })
      .then((ws) => {
        set((s) => ({ byId: { ...s.byId, [ideaId]: ws } }));
        return ws;
      })
      .catch(() => null)
      .finally(() => inflight.delete(ideaId));
    inflight.set(ideaId, p);
    return p;
  },
}));

/** Refresh an idea's workspace from anywhere (after a step finishes). */
export function refreshIdeaWorkspace(ideaId: string) {
  return useIdeaWorkspaceStore.getState().refresh(ideaId);
}

/** The workspace for an idea, loading it on first use. */
export function useIdeaWorkspace(ideaId: string | null): IdeaWorkspace | null {
  const ws = useIdeaWorkspaceStore((s) => (ideaId ? s.byId[ideaId] ?? null : null));
  const load = useIdeaWorkspaceStore((s) => s.load);
  useEffect(() => {
    if (ideaId) load(ideaId);
  }, [ideaId, load]);
  return ws;
}

/**
 * Keep the current idea's workspace fresh: quietly on every move between its
 * steps (statuses change as work finishes) and when the tab comes back into
 * view (a memo compiled, a checkout returned).
 */
export function useIdeaScopeSync(ideaId: string | null, routeKey: string) {
  useEffect(() => {
    if (!ideaId) return;
    const t = window.setTimeout(() => void refreshIdeaWorkspace(ideaId), 300);
    return () => window.clearTimeout(t);
  }, [ideaId, routeKey]);
  useEffect(() => {
    if (!ideaId) return;
    const onVis = () => document.visibilityState === 'visible' && void refreshIdeaWorkspace(ideaId);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [ideaId]);
}
